import { Link } from "react-router-dom";
import { ArrowRight, Sun, Wrench, Droplets, ShieldCheck, Cog, Workflow, Phone, CheckCircle2, Award, Sparkles, Star } from "lucide-react";
import { IMAGES, SERVICES, VALUES, COMPANY } from "../lib/constants";
import SectionHeading from "../components/SectionHeading";
import Testimonials from "../components/Testimonials";
import TrustBar from "../components/TrustBar";
import SEO from "../components/SEO";

const iconMap = { Sun, Wrench, Droplets, ShieldCheck, Cog, Workflow };

export default function Home() {
  return (
    <div data-testid="page-home">
      <SEO
        title="20 Anni di Esperienza nel Fotovoltaico"
        description="Impresa Edile Lici: progettazione, installazione e manutenzione di impianti fotovoltaici civili e industriali. Lavaggio professionale pannelli. Sede Seveso (MB), Lombardia."
        path="/"
        image={IMAGES.heroLuxury}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#0F5132]">
        <img
          src={IMAGES.heroLuxury}
          alt="Villa di lusso con impianto fotovoltaico"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F5132]/85 via-[#0F5132]/60 to-[#0F5132]/20" />
        <div className="absolute inset-0 hero-overlay opacity-60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-24 w-full">
          <div className="max-w-3xl">
            {/* Pre-headline trust pill */}
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/25 rounded-full pl-3 pr-5 py-2 mb-7 animate-fade-up">
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="h-3.5 w-3.5 fill-[#FED7AA] text-[#FED7AA]" />
                ))}
              </div>
              <span className="text-xs sm:text-sm text-white font-medium">
                500+ impianti installati • Lombardia
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-serif text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.02] animate-fade-up delay-100"
            >
              20 Anni di Esperienza
              <span className="block italic text-[#FED7AA] mt-2">al Tuo Servizio.</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-white/90 max-w-2xl leading-relaxed animate-fade-up delay-200">
              Progettiamo, installiamo e manteniamo impianti fotovoltaici di ogni dimensione, su
              tetto e a terra. Specialisti nel lavaggio professionale e nella manutenzione per
              garantire sempre la massima resa energetica.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up delay-300">
              <Link
                to="/contatti"
                data-testid="hero-cta-preventivo"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-[#D97706] text-white font-medium hover:bg-[#B45309] transition-all hover:translate-y-[-2px] shadow-xl shadow-black/30"
              >
                Richiedi un Preventivo Gratuito
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/servizi"
                data-testid="hero-cta-servizi"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white/10 backdrop-blur-md text-white font-medium border border-white/35 hover:bg-white/20 transition-all"
              >
                Scopri i Nostri Servizi
              </Link>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl animate-fade-up delay-400 border-t border-white/15 pt-10">
            {[
              { v: "20+", l: "Anni di esperienza" },
              { v: "500+", l: "Impianti installati" },
              { v: "25%", l: "Resa recuperata col lavaggio" },
              { v: "100%", l: "Sicurezza certificata" },
            ].map((s) => (
              <div key={s.l} className="text-white">
                <div className="font-serif text-4xl md:text-5xl text-[#FED7AA]">{s.v}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/75 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <TrustBar />

      {/* CHI SIAMO */}
      <section className="py-20 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative">
              <img
                src={IMAGES.industrial}
                alt="Tetto industriale con fotovoltaico"
                className="rounded-2xl shadow-xl w-full aspect-[4/5] object-cover"
              />
              <div className="hidden md:block absolute -bottom-8 -right-8 bg-white p-6 rounded-2xl shadow-xl border border-[#E5E7EB] max-w-xs">
                <Award className="h-8 w-8 text-[#D97706] mb-3" />
                <div className="font-serif text-2xl text-[#0F5132]">Oltre 500</div>
                <div className="text-sm text-[#4B5563] mt-1">
                  impianti realizzati e manutenuti in Lombardia
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <SectionHeading
              eyebrow="Chi Siamo"
              title="Oltre 20 anni di eccellenza nel fotovoltaico"
              description="Da più di due decenni siamo il punto di riferimento per l'efficienza energetica. Affidabilità, competenza e aggiornamento continuo sono i pilastri che ci hanno permesso di crescere insieme ai nostri clienti. Scegliere noi significa avere un partner solido, in grado di gestire l'intero ciclo di vita del vostro impianto."
            />
            <ul className="mt-8 space-y-4">
              {[
                "Progettazione su misura per residenziale e industriale",
                "Gestione completa delle pratiche burocratiche",
                "Squadre interne formate per i lavori in quota",
                "Partner di marchi leader del settore fotovoltaico",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[#111827]">
                  <CheckCircle2 className="h-5 w-5 text-[#0F5132] mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Link
                to="/chi-siamo"
                data-testid="home-chi-siamo-link"
                className="inline-flex items-center gap-2 text-[#0F5132] font-medium hover:gap-3 transition-all"
              >
                Scopri di più su di noi <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <SectionHeading
            eyebrow="I Nostri Servizi"
            title="Tutto ciò che serve al tuo impianto, da un unico partner"
            description="Dalla progettazione al lavaggio dei pannelli: un servizio a 360° che protegge l'investimento e ne massimizza la resa."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {SERVICES.map((s, idx) => {
              const Icon = iconMap[s.icon];
              const span = idx === 0 ? "md:col-span-7" : idx === 1 ? "md:col-span-5" : "md:col-span-12";
              return (
                <article
                  key={s.id}
                  data-testid={`service-card-${s.id}`}
                  className={`group ${span} relative overflow-hidden rounded-2xl bg-[#F3F4F6] border border-[#E5E7EB] hover:shadow-lg hover:-translate-y-1 transition-all duration-300`}
                >
                  <div className="grid md:grid-cols-2 h-full">
                    <div className="relative h-64 md:h-full overflow-hidden">
                      <img
                        src={s.image}
                        alt={s.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    </div>
                    <div className="p-7 md:p-9 flex flex-col">
                      <div className="h-12 w-12 rounded-xl bg-[#0F5132]/10 text-[#0F5132] flex items-center justify-center mb-5">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="text-xs uppercase tracking-widest text-[#D97706] font-medium">
                        {s.subtitle}
                      </div>
                      <h3 className="font-serif text-2xl text-[#0F5132] mt-2">{s.title}</h3>
                      <p className="mt-3 text-sm text-[#4B5563] leading-relaxed">{s.description}</p>
                      <ul className="mt-5 space-y-2">
                        {s.highlights.slice(0, 3).map((h) => (
                          <li key={h} className="flex items-start gap-2 text-sm text-[#111827]">
                            <CheckCircle2 className="h-4 w-4 text-[#0F5132] mt-0.5 flex-shrink-0" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/servizi"
                        data-testid={`service-link-${s.id}`}
                        className="mt-6 inline-flex items-center gap-2 text-[#0F5132] font-medium text-sm hover:gap-3 transition-all"
                      >
                        Vedi dettagli <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* VALORE AGGIUNTO */}
      <section className="py-20 md:py-32 bg-[#0F5132] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={IMAGES.workerSafety} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="accent-line" />
              <span className="text-xs uppercase tracking-[0.25em] text-[#FED7AA] font-medium">
                Il Nostro Valore Aggiunto
              </span>
            </div>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight">
              Perché scegliere Impresa Edile Lici
            </h2>
            <p className="mt-5 text-white/80 text-base md:text-lg">
              Tre pilastri che fanno la differenza ad ogni intervento.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {VALUES.map((v) => {
              const Icon = iconMap[v.icon];
              return (
                <div
                  key={v.title}
                  data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="p-8 rounded-2xl bg-white/5 backdrop-blur-md border border-white/15 hover:bg-white/10 transition-colors"
                >
                  <div className="h-12 w-12 rounded-xl bg-[#D97706]/20 text-[#FED7AA] flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-serif text-xl mb-3">{v.title}</h3>
                  <p className="text-sm text-white/80 leading-relaxed">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIANZE */}
      <Testimonials />

      {/* CTA FINALE */}
      <section className="py-20 md:py-28 bg-[#FAFAFA]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <Sparkles className="h-10 w-10 text-[#D97706] mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-5xl text-[#0F5132] leading-tight">
            Massimizza l'efficienza del tuo impianto.
          </h2>
          <p className="mt-6 text-base md:text-lg text-[#4B5563] max-w-2xl mx-auto">
            Contattaci per una consulenza gratuita. Il nostro team è a tua disposizione per trovare
            la soluzione su misura per te.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contatti"
              data-testid="cta-final-preventivo"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#0F5132] text-white font-medium hover:bg-[#0A3622] transition-all hover:-translate-y-0.5 shadow-lg"
            >
              Richiedi un Preventivo
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="cta-final-call"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-[#0F5132] text-[#0F5132] font-medium hover:bg-[#0F5132] hover:text-white transition-all"
            >
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
